const MAX_INPUT_LENGTH = 10000000

const isValidInput = (text: string): boolean => {
  return typeof text === 'string' && text.length <= MAX_INPUT_LENGTH
}

const isPresignedUrl = (url: string): boolean => {
  const q = url.indexOf('?')
  if (q === -1) return false
  const query = url.substring(q + 1)
  return (
    query.includes('X-Amz-Signature=') ||
    query.includes('Signature=') ||
    query.includes('x-amz-signature=') ||
    query.includes('signature=')
  )
}

const ensureQueryPlusEncoded = (url: string): string => {
  if (isPresignedUrl(url)) {
    return url
  }
  const q = url.indexOf('?')
  if (q === -1) return url
  const path = url.substring(0, q)
  const query = url.substring(q + 1)
  return path + '?' + query.replace(/\+/g, '%2B')
}

const safeDecodeUrl = (url: string): string => {
  try {
    const idxEnc = url.indexOf('%3F')
    const idxLit = url.indexOf('?')
    const queryStart =
      idxEnc === -1 ? idxLit : idxLit === -1 ? idxEnc : Math.min(idxEnc, idxLit)
    if (queryStart !== -1) {
      const pathPart = url.substring(0, queryStart)
      const queryPart = url.substring(queryStart)
      const decodedPath = decodeURIComponent(pathPart)
      const queryOnly = queryPart.startsWith('%3F')
        ? queryPart.slice(3)
        : queryPart.slice(1)
      return decodedPath + '?' + queryOnly
    }
    return decodeURIComponent(url)
  } catch {
    return url
  }
}

const extractEncodedMarkdownLink = (
  href: string
): { linkText: string; url: string } | null => {
  const openTag = '%5D%28'
  const openIdx = href.indexOf('%5B')
  const midIdx = href.indexOf(openTag)
  const closeIdx = href.lastIndexOf('%29')
  if (
    openIdx === 0 &&
    midIdx !== -1 &&
    closeIdx !== -1 &&
    closeIdx > midIdx + openTag.length
  ) {
    try {
      const linkText = decodeURIComponent(href.substring(openIdx + 3, midIdx))
      const urlPart = safeDecodeUrl(
        href.substring(midIdx + openTag.length, closeIdx)
      )
      if (isPresignedUrl(urlPart)) {
        return null
      }
      return { linkText, url: urlPart }
    } catch {
      return null
    }
  }
  return null
}

const extractUrlFromMarkdownPattern = (
  href: string,
  startChar: '(' | '%28'
): string | null => {
  const startIdx = href.indexOf(startChar)
  if (startIdx === -1) return null

  let depth = 0
  let endIdx = -1
  const isEncoded = startChar === '%28'

  for (let i = startIdx; i < href.length; i++) {
    if (isEncoded) {
      if (href.substring(i).startsWith('%28')) {
        depth++
        i += 2
      } else if (href.substring(i).startsWith('%29')) {
        depth--
        if (depth === 0) {
          endIdx = i
          break
        }
        i += 2
      }
    } else {
      if (href[i] === '(') depth++
      if (href[i] === ')') {
        depth--
        if (depth === 0) {
          endIdx = i
          break
        }
      }
    }
  }

  if (endIdx === -1) return null
  const startOffset = isEncoded ? 3 : 1
  const urlCandidate = href.substring(startIdx + startOffset, endIdx)
  if (isEncoded) {
    try {
      const decoded = safeDecodeUrl(urlCandidate)
      if (isPresignedUrl(decoded)) {
        return null
      }
      return decoded.match(/^https?:\/\//) ? decoded : null
    } catch {
      return null
    }
  }
  if (isPresignedUrl(urlCandidate)) {
    return null
  }
  return urlCandidate.match(/^https?:\/\//) ? urlCandidate : null
}

export const preprocessMarkdown = (text: string): string => {
  if (!text) return ''

  if (!isValidInput(text)) {
    console.warn('preprocessMarkdown: Input exceeds maximum length, truncating')
    text = text.substring(0, MAX_INPUT_LENGTH)
  }

  text = text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')

  text = text.replace(
    /<a\s+[^>]*href=["']([^"']+)["'][^>]*>/gi,
    (match, href) => {
      if (isPresignedUrl(href)) {
        return match
      }
      const fixedHref = fixMalformedHref(href)
      return fixedHref !== href ? match.replace(href, fixedHref) : match
    }
  )

  text = text.replace(
    /%5B([^%]*?)%5D\((https?:\/\/[^\)]+)\)/gi,
    (match, linkText, url) => {
      if (isPresignedUrl(url)) {
        return match
      }
      try {
        if (linkText.length > 1000) {
          console.warn(
            'preprocessMarkdown: Link text too long, skipping decode',
            {
              length: linkText.length,
            }
          )
          return `[${linkText}](${url})`
        }
        const decodedText = decodeURIComponent(linkText)
        return `[${decodedText}](${url})`
      } catch (error) {
        console.warn('preprocessMarkdown: Failed to decode link text', {
          error: error instanceof Error ? error.message : String(error),
          linkText: linkText.substring(0, 100),
        })
        return `[${linkText}](${url})`
      }
    }
  )

  text = text.replace(
    /%5B([^%]*?)%5D%28(https?%3A%2F%2F[\s\S]+)%29/gi,
    (match, linkText, encodedUrl) => {
      try {
        const decodedUrl = safeDecodeUrl(encodedUrl)
        if (isPresignedUrl(decodedUrl)) {
          return match
        }
        const decodedText = decodeURIComponent(linkText)
        return `[${decodedText}](${ensureQueryPlusEncoded(decodedUrl)})`
      } catch (error) {
        console.warn(
          'preprocessMarkdown: Failed to decode fully encoded link',
          {
            error: error instanceof Error ? error.message : String(error),
          }
        )
        return match
      }
    }
  )

  if (
    text.includes('%5B') &&
    text.includes('%5D') &&
    !text.includes('%3F') &&
    !text.includes('%26')
  ) {
    try {
      const decoded = decodeURIComponent(text)
      if (
        decoded.length <= MAX_INPUT_LENGTH &&
        decoded.match(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/)
      ) {
        text = decoded
      }
    } catch (error) {
      console.warn(
        'preprocessMarkdown: Failed to decode URL-encoded markdown',
        {
          error: error instanceof Error ? error.message : String(error),
          textLength: text.length,
        }
      )
    }
  }

  let md = text.replace(/\r\n?|\r/g, '\n')

  md = md.replace(/\s*\[[A-Za-z][A-Za-z\s]*(?:\.{3}|…)\]\s*/g, ' ')

  const textLines = md.split('\n')
  const filteredLines = textLines.filter(line => {
    const trimmed = line.trim()
    const stripped = trimmed.replace(/^[^A-Za-z0-9]+/, '')
    const isS3Header = /^(#{1,6}\s*)?(s3\s*storage\s*locations)\s*:?\s*$/i.test(
      stripped
    )
    const isOnlyPunct = /^(\.{1,3}|•|-)\s*$/.test(trimmed)
    // Thematic breaks (---, ***, ___) render as a horizontal rule. Agents emit
    // them as section separators, which look like stray lines in the chat — and
    // worse, they flash mid-stream because a partial chunk ending in `---` is
    // parsed as a rule before the following heading text arrives. Drop them.
    // (Table delimiter rows are `|---|` and start with `|`, so they're safe.)
    const isThematicBreak = /^([-*_])(\s*\1){2,}$/.test(trimmed)
    return (
      !trimmed.toLowerCase().includes('thinking...') &&
      !trimmed.includes('[Processing your request...]') &&
      !isS3Header &&
      !isOnlyPunct &&
      !isThematicBreak
    )
  })
  md = filteredLines.join('\n')

  md = md.replace(/s3:\/\/[^\s<>'\"]+/g, '')

  const urlRegex = /(https?:\/\/[^\s<>'\"\)]+)/g
  let lastIndex = 0
  const parts: string[] = []

  for (const match of md.matchAll(urlRegex)) {
    const url = match[0]
    const matchIndex = match.index!

    parts.push(md.substring(lastIndex, matchIndex))

    if (isPresignedUrl(url)) {
      parts.push(url)
    } else {
      const beforeUrl = md.substring(0, matchIndex)
      const alreadyInMarkdownLink = /\]\s*\(\s*$/.test(beforeUrl)

      if (alreadyInMarkdownLink) {
        parts.push(ensureQueryPlusEncoded(url))
      } else {
        const lastBracket = beforeUrl.lastIndexOf('[')
        const lastCloseBracket = beforeUrl.lastIndexOf(']')
        const lastParen = beforeUrl.lastIndexOf('(')
        const isInsideLink =
          lastBracket > lastCloseBracket && lastParen > lastBracket

        if (isInsideLink) {
          parts.push(ensureQueryPlusEncoded(url))
        } else {
          const displayText = extractUrlDisplayText(url)
          parts.push(`[${displayText}](${ensureQueryPlusEncoded(url)})`)
        }
      }
    }

    lastIndex = matchIndex + url.length
  }

  parts.push(md.substring(lastIndex))
  md = parts.join('')

  md = md
    .split('\n')
    .map(line => line.replace(/^(#{1,6})([^\s#])/, '$1 $2'))
    .map(line => line.replace(/^([*\-])(?![*\-])(\S)/, '$1 $2'))
    .join('\n')

  md = md.replace(/\n{3,}/g, '\n\n')

  md = md.replace(/\n(#{1,6}\s)/g, '\n\n$1')
  md = md.replace(/([^\n])(#\s)/g, '$1\n\n$2')

  // A GFM table only renders when its header row starts its own block. Agents
  // sometimes glue the header onto preceding prose (e.g. "...details.| Company
  // | Amount |"), which makes the whole thing render as raw pipes. When a
  // delimiter row (|---|---|) follows a line whose table cells are preceded by
  // other text, split that prose off so the header row begins a clean block.
  const tableLines = md.split('\n')
  const separated: string[] = []
  const delimiterRow = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/
  for (let i = 0; i < tableLines.length; i++) {
    const line = tableLines[i]
    const firstPipe = line.indexOf('|')
    const nextIsDelimiter = delimiterRow.test(tableLines[i + 1] ?? '')
    if (
      nextIsDelimiter &&
      firstPipe > 0 &&
      /\S/.test(line.slice(0, firstPipe))
    ) {
      separated.push(
        line.slice(0, firstPipe).trimEnd(),
        '',
        line.slice(firstPipe)
      )
    } else {
      separated.push(line)
    }
  }
  md = separated.join('\n')

  md = md.replace(/([^\n])\n(?!\n)/g, '$1  \n')

  return md.trim()
}

const extractUrlDisplayText = (url: string): string => {
  try {
    const urlObj = new URL(url)
    const hostname = urlObj.hostname.replace(/^www\./, '')
    const pathname = urlObj.pathname

    if (pathname && pathname !== '/') {
      const pathSegments = pathname.split('/').filter(Boolean)
      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1]
        if (lastSegment.length < 50 && !lastSegment.match(/^\d+$/)) {
          return `${hostname}/${lastSegment}`
        }
      }
    }

    return hostname
  } catch {
    const match = url.match(/https?:\/\/(?:www\.)?([^\/\s]+)/)
    return match ? match[1] : 'Link'
  }
}

export const shortenLinkText = (href: string, text: string) => {
  if (!href) return text

  if (text === href || text.trim() === href.trim()) {
    return extractUrlDisplayText(href)
  }

  const isLongUrl = href.length > 150
  const isLongText = text.length > 100
  if (!(isLongUrl || isLongText)) return text
  if (
    href.includes('s3.amazonaws.com') ||
    href.includes('s3://') ||
    href.includes('/report')
  ) {
    return 'Download report'
  }
  if (/click|here/i.test(text)) {
    return 'Click here to download'
  }
  if (href.length > 200) {
    return 'Download link'
  }
  return text
}

export const fixMalformedHref = (href: string): string => {
  if (!href) return href

  if (isPresignedUrl(href)) {
    return href
  }

  const MAX_HREF_LENGTH = 10000
  if (href.length > MAX_HREF_LENGTH) {
    console.warn('fixMalformedHref: Href exceeds maximum length', {
      length: href.length,
      maxLength: MAX_HREF_LENGTH,
    })
    return href
  }

  const hasEncodedMarkdown =
    href.includes('%5B') ||
    href.includes('%5D') ||
    href.includes('%28') ||
    href.includes('%29')

  if (hasEncodedMarkdown) {
    const encodedLink = extractEncodedMarkdownLink(href)
    if (encodedLink) {
      const markdownLinkMatch =
        `[${encodedLink.linkText}](${encodedLink.url})`.match(
          /\[([^\]]*?)\]\((https?:\/\/[^\)]+)\)/
        )
      if (markdownLinkMatch?.[2]) {
        return ensureQueryPlusEncoded(markdownLinkMatch[2])
      }
    }

    if (href.includes('%3F') || href.includes('%26')) {
      try {
        const decoded = safeDecodeUrl(href)
        if (decoded.match(/^https?:\/\//)) {
          return ensureQueryPlusEncoded(decoded)
        }
      } catch (error) {
        console.warn('fixMalformedHref: Failed to decode href', {
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    if (href.includes('%5B') && href.includes('%5D')) {
      const urlFromPattern = extractUrlFromMarkdownPattern(href, '(')
      if (urlFromPattern) {
        return ensureQueryPlusEncoded(urlFromPattern)
      }

      const urlFromEncoded = extractUrlFromMarkdownPattern(href, '%28')
      if (urlFromEncoded) {
        return ensureQueryPlusEncoded(urlFromEncoded)
      }

      const match = href.match(/%5B[^%]*?%5D\((https?:\/\/[^\)]+)\)/)
      if (match?.[1]) {
        return ensureQueryPlusEncoded(match[1])
      }

      const urlMatch = href.match(/\((https?:\/\/[^\)]+)\)/)
      if (urlMatch?.[1]) {
        return ensureQueryPlusEncoded(urlMatch[1])
      }
    }

    try {
      const decoded = decodeURIComponent(href)
      const markdownLinkMatch = decoded.match(
        /\[([^\]]*?)\]\((https?:\/\/[^\)]+)\)/
      )
      if (markdownLinkMatch?.[2]) {
        return ensureQueryPlusEncoded(markdownLinkMatch[2])
      }
      if (decoded.match(/^https?:\/\//)) {
        return ensureQueryPlusEncoded(decoded)
      }
    } catch (error) {
      console.warn('fixMalformedHref: Failed to decode href', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  } else {
    const markdownLinkMatch = href.match(/\[([^\]]*?)\]\((https?:\/\/[^\)]+)\)/)
    if (markdownLinkMatch?.[2]) {
      return ensureQueryPlusEncoded(markdownLinkMatch[2])
    }

    if (href.match(/^https?:\/\//)) {
      return ensureQueryPlusEncoded(href)
    }
  }

  return href
}
